import type { ToggleModalProps } from '../../../../../components/modal/types';
import type Locale from '@whizz/api-client/dist/@types/model/Locale';
import type { AxiosError } from 'axios';
import { repository } from '@whizz/api-client';
import { Translate, useI18n } from '@whizz/react-i18n';
import { Formik } from 'formik';
import { useRef } from 'react';
import { useMutation } from 'react-query';
import { Alert, Button, ButtonIcon } from '../../../../../components';
import { SaveButton } from '../../../../../global';
import { getIconComponent } from '../../../../../util/reactFunc';
import { GLOBAL_ICONS } from '../../../../../util/vars';
import { API_ERRORS } from '../../../../../util/constants';

export interface UploadParentsModalProps extends ToggleModalProps {
  onSuccess: () => void;
}

const templateFiles: Record<Locale['id'], string> = {
  'en-gb': 'en-gb/Maths_Whizz_Parent_Upload_Template.xlsx',
  'en-nz': 'en-nz/Maths_Whizz_Parent_Upload_Template.xlsx',
};

const Upload = ({ toggle, onSuccess }: UploadParentsModalProps): JSX.Element => {

  const { locale, translate } = useI18n();

  return (
    <div className={'container'}>
      <ol className={'list-decimal ml-6 space-y-6'}>
        <li className={'space-y-4'}>
          <h4>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.STEP_1')}</h4>
          <div>
            <a href={`/downloads/${(templateFiles[locale?.id ?? 'en-gb'] ?? 'en-gb')}`} download>
              <Button color={'positive'} isOutline icon={GLOBAL_ICONS.download}>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.BUTTONS.TEMPLATE')}</Button>
            </a>
          </div>
        </li>
        <li>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.STEP_2')}</li>
        <li className={'space-y-4'}>
          <h4>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.STEP_3')}</h4>
          <FileUploadForm
            onSuccess={() => {
              onSuccess();
              toggle();
            }}
          />
        </li>
      </ol>
    </div>
  );
};

interface FileUploadForm {
  onSuccess: () => void;
}

type FileUploadErrorGuardian = {
  name: string,
  email: string,
};

type FileUploadErrorContextShape = {
  upn: string,
  childName: string,
  guardians: FileUploadErrorGuardian[],
};

type FileUploadErrorContext = {
  missingHeaders?: string[],
  invalidEmails?: FileUploadErrorContextShape[],
  invalidNames?: FileUploadErrorContextShape[],
  invalidUpns?: FileUploadErrorContextShape[],
  overLimit?: FileUploadErrorContextShape[],
  duplicateUPNs?: string[],
  duplicateEmails?: FileUploadErrorContextShape[],
};

type FileUploadError = AxiosError<{ error: string, context: FileUploadErrorContext }> | null;

const FileUploadForm = ({ onSuccess }: FileUploadForm): JSX.Element => {
  const { locale, translate } = useI18n();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation(
    ({ files }: { files: FileList }) => repository.ParentUploadRepository.addFiles(locale?.id ?? 'en-gb', files),
  );


  const ShowUploadError = (error: FileUploadError): React.ReactNode => {
    if (!error || null == error.response || !error.response.data) {
      return null;
    }

    let errorMessage = '';

    const responseErrorMessage = error?.response?.data?.error;

    switch (responseErrorMessage) {
      case API_ERRORS.GUARDIANS_IMPORT_EMPTY: errorMessage = 'IMPORT_EMPTY'; break;
      case API_ERRORS.GUARDIANS_IMPORT_ERROR: errorMessage = 'IMPORT_ERROR'; break;
      case API_ERRORS.GUARDIANS_IMPORT_HEADERS: errorMessage = 'IMPORT_HEADERS'; break;
      case API_ERRORS.GUARDIANS_IMPORT_EMAIL: errorMessage = 'IMPORT_EMAIL'; break;
      case API_ERRORS.GUARDIANS_IMPORT_UPN: errorMessage = 'IMPORT_UPN'; break;
      default: errorMessage = 'IMPORT_UNKNOWN'; break;
    }

    const context = error?.response?.data?.context ?? {};

    return (
      <div className='mt-4 space-y-4'>
        <h3 className={'font-bold text-lg'}>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.line1')}</h3>
        <p>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.line2')}</p>
        <Alert show type='error'>
          <p className={'font-bold whitespace-pre-line'}>
            <Translate i18nKey={`pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.messages.${errorMessage}`}>
              {/**
             * eslint-disable-next-line @typescript-eslint/ban-ts-comment
             * @ts-ignore **/ }
              <a href={`mailto:${translate('global.SUPPORT_EMAIL')}`} className={'underline'} target={'_blank'} />
            </Translate>
          </p>
          <div className={'space-y-4'}>
            {context.missingHeaders && context.missingHeaders.length ? <InvalidComponent translateKey={'MISSING_HEADERS'} invalid={context.missingHeaders} /> : null}
            {context.invalidUpns && context.invalidUpns.length ? <InvalidComponent translateKey={'INVALID_UPNS'} invalid={context.invalidUpns} /> : null}
            {context.invalidNames && context.invalidNames.length ? <InvalidComponent translateKey={'INVALID_NAMES'} invalid={context.invalidNames} /> : null}
            {context.invalidEmails && context.invalidEmails.length ? <InvalidComponent translateKey={'INVALID_EMAILS'} invalid={context.invalidEmails} guardianPropToShow={'email'} /> : null}
            {context.overLimit && context.overLimit.length ? <InvalidComponent translateKey={'TOO_MANY'} invalid={context.overLimit} /> : null}
            {context.duplicateUPNs && context.duplicateUPNs.length ? <InvalidComponent translateKey={'DUPLICATE_UPNS'} invalid={context.duplicateUPNs} /> : null}
            {context.duplicateEmails && context.duplicateEmails.length ? <InvalidComponent translateKey={'DUPLICATE_EMAILS'} guardianPropToShow={'email'} invalid={context.duplicateEmails} /> : null}
          </div>
        </Alert>
        <p className={'font-bold'}>
          <Translate i18nKey={'pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.line3'}>
            {/**
             * eslint-disable-next-line @typescript-eslint/ban-ts-comment
             * @ts-ignore **/ }
            <a href={`mailto:${translate('global.SUPPORT_EMAIL')}`} className={'underline'} target={'_blank'} />
          </Translate>
        </p>
      </div>
    );
  };

  return (
    <Formik<{ files: FileList | null }>
      onSubmit={(values, { resetForm }) => {
        mutation.mutate(
          { files: values.files! },
          {
            onSuccess: () => {
              onSuccess();
              resetForm();
              if (inputFileRef.current) {
                inputFileRef.current.value = '';
              }
            },
            onError: () => {
              resetForm();
              if (inputFileRef.current) {
                inputFileRef.current.value = '';
              }
            },
          },
        );
      }}
      initialValues={{ files: null }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm }) => {
        const handleResetForm = () => {
          resetForm();
          if (inputFileRef.current) {
            inputFileRef.current.value = '';
          }
        };

        return (
          <form onSubmit={handleSubmit}>
            <div className={'flex justify-between items-center space-x-2'}>
              {(null != values.files && 0 < values.files.length) ? (
                <>
                  <div className={'flex items-center space-x-2'}>
                    {getIconComponent('Attachment')}<span className={'ml-2 w-72 truncate'}>{values.files[0].name}</span>
                    <ButtonIcon icon={GLOBAL_ICONS.delete} size={'sm'} isOutline isCircle color={'light'} onClick={() => handleResetForm()} />
                  </div>
                </>
              ) : (
                <SaveButton
                  onClick={() => inputFileRef?.current?.click()}
                >
                  {translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.BUTTONS.SELECT')}
                </SaveButton>
              )}
              <input
                ref={inputFileRef}
                type="file"
                className={'hidden'}
                onChange={(event) => setFieldValue('files', event.target.files)}
              />
              <Button
                type={'submit'}
                icon={'Publish'}
                color={'positive'}
                isOutline
                disabled={null == values.files || 0 === values.files.length || mutation.isLoading}
              >
                {translate(`pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.BUTTONS.${mutation.isLoading ? 'SENDING' : 'SEND'}`)}
              </Button>
            </div>
            {ShowUploadError(mutation.error as FileUploadError)}
          </form>
        );}}
    </Formik>
  );
};

const InvalidComponent = ({ translateKey, invalid, guardianPropToShow }: { translateKey: 'MISSING_HEADERS' | 'DUPLICATE_UPNS' | 'DUPLICATE_EMAILS' | 'INVALID_UPNS' | 'INVALID_NAMES' | 'INVALID_EMAILS' | 'TOO_MANY', invalid: (FileUploadErrorContextShape | string)[], guardianPropToShow?: keyof FileUploadErrorGuardian } ) => {
  const { translate } = useI18n();
  return (
    <div>
      <h4 className={'font-bold'}>{translate(`pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.messages.${translateKey}`)}</h4>
      <ul key={translateKey}>
        {[ 'DUPLICATE_UPNS', 'MISSING_HEADERS' ].includes(translateKey) ?
          (<>{invalid.map((row, index) => (<li key={`${translateKey}${index}`}>{translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.messages.ROW', { upn: row })}</li>))}</>)
          : (invalid as FileUploadErrorContextShape[]).map((row, index) => (
            <li key={`${translateKey}${index}`}>
              {guardianPropToShow ? (
                <>
                  {translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.messages.ROW_WITH_DATA', {
                    count: row.guardians.length,
                    upn: row.upn,
                    name: row.childName,
                    data: row.guardians[0][guardianPropToShow],
                  })}
                  {row.guardians.length > 1 ? (
                    <ul className="list-disc pl-5">
                      {row.guardians.map((guardian, index) => (
                        <li key={index}>{guardian[guardianPropToShow]}</li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <>
                  {translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.ERROR.messages.ROW', {
                    upn: row.upn,
                    name: row.childName,
                  })}
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
};

export default Upload;
